import { useContext } from 'react'

import SubscriptionGiftRepository from './subscription-gift'
import { GiftBanners, GiftCard, ServiceResponse, SubscriptionGift } from '../../types'
import { ActionTypes } from '../../stores/App/AppReducer'
import { AppContext } from '../../stores/App/AppStore'
import { starterDataError } from '../../helpers/utils/error-handling'

const useSubscriptionGiftRepository = () => {
    const { dispatch } = useContext(AppContext)

    const subscriptionGiftRepository = new SubscriptionGiftRepository()

    const retrieveGiftCards = () => {
        const giftCardResponse: ServiceResponse<GiftCard[]> = {}

        subscriptionGiftRepository.retrieveGiftCards()
            .then(giftCards => {
                giftCardResponse.data = giftCards
            })
            .catch(error => {
                giftCardResponse.error = starterDataError(error)
            })
            .finally(() => {
                dispatch({
                    type: ActionTypes.GIFT_CARDS_RETRIEVED,
                    data: giftCardResponse.data ?? [],
                })
            })
    }

    const retrieveGiftBanners = () => {
        const giftBannerResponse: ServiceResponse<GiftBanners> = {}

        subscriptionGiftRepository.retrieveGiftBanners()
            .then(giftBanners => {
                giftBannerResponse.data = giftBanners
            })
            .catch(error => {
                giftBannerResponse.error = starterDataError(error)
            })
            .finally(() => {
                dispatch({
                    type: ActionTypes.GIFT_BANNERS_RETRIEVED,
                    giftBanners: giftBannerResponse,
                })
            })
    }

    const createGiftPaymentOrder = async (subscriptionGift: SubscriptionGift) => {
        const paymentIntentResponse: ServiceResponse<string> = {}
        return subscriptionGiftRepository.createSubscriptionGift(subscriptionGift)
            .then((paymentIntentClientSecret: string) => {
                paymentIntentResponse.data = paymentIntentClientSecret
            })
            .catch((error) => {
                throw starterDataError(error)
            })
            .finally(() => {
                dispatch({
                    type: ActionTypes.GIFT_PAYMENT_INTENT_CREATED,
                    paymentIntentClientSecret: paymentIntentResponse.data ?? ``,
                })
            })
    }

    const updateGiftAndStripePaymentOrderOnSuccess = async (giftCode: string) => {
        return subscriptionGiftRepository.updateGiftAndStripePaymentOrderOnSuccess(giftCode)
            .catch((error) => {
                throw starterDataError(error)
            })
    }

    const retrieveSubscriptionGift = async (giftCode: string): Promise<SubscriptionGift> => {
        return subscriptionGiftRepository.retrieveSubscriptionGift(giftCode)
    }

    const setupStripeSubscriptionFromSubscriptionGift = async (giftCode: string, exchangeId?: string): Promise<string> => {
        return subscriptionGiftRepository.setupStripeSubscriptionFromSubscriptionGift(giftCode, exchangeId)
    }

    const linkMaxAgeGiftToMembership = async (giftCode: string, exchangeId?: string): Promise<string> => {
        return subscriptionGiftRepository.linkMaxAgeGiftToMembership(giftCode, exchangeId)
    }

    const setupStripeIntent = async (exchangeId: string): Promise<string> => {
        return subscriptionGiftRepository.setupStripeIntent(exchangeId)
    }

    return {
        retrieveGiftCards,
        retrieveGiftBanners,
        createGiftPaymentOrder,
        updateGiftAndStripePaymentOrderOnSuccess,
        retrieveSubscriptionGift,
        setupStripeSubscriptionFromSubscriptionGift,
        setupStripeIntent,
        linkMaxAgeGiftToMembership,
    }
}

export { useSubscriptionGiftRepository }